// Number and date formatting for the Usage demo. Amounts are USD a year;
// millions keep one decimal ("$6.0M") so a commitment reads as the spec
// writes it, which the app's suffix formatter (it drops ".0") does not.

import { DEMO_TODAY } from "./fixtures";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "$2.9M", "$68K", "$684". */
export const money = (amount: number): string => {
    const abs = Math.abs(amount);
    const sign = amount < 0 ? "-" : "";
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}K`;
    return `${sign}$${Math.round(abs)}`;
};

/** As `money`, without a ".0": "$31M", for a headline where the decimal reads as noise. */
export const moneyShort = (amount: number): string => money(amount).replace(/\.0M$/, "M");

/** "12,400". */
export const int = (n: number): string => Math.round(n).toLocaleString("en-US");

/** "+350" / "-550", for a waterfall step. */
export const signedInt = (n: number): string => (n > 0 ? `+${int(n)}` : `−${int(-n)}`);

export const pct = (n: number): string => `${Math.round(n)}%`;

const parse = (iso: string): Date => {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d));
};

/** "21 Nov", with a non-breaking space so a cell never splits it. */
export const shortDate = (iso: string): string => {
    const d = parse(iso);
    return `${d.getUTCDate()}\u00a0${MONTHS[d.getUTCMonth()]}`;
};

/** "21 Nov 2026". */
export const longDate = (iso: string): string => `${shortDate(iso)} ${parse(iso).getUTCFullYear()}`;

/** "Nov 2026", for a month given as "2026-11". */
export const monthLabel = (yearMonth: string): string => {
    const [y, m] = yearMonth.split("-").map(Number);
    return `${MONTHS[m - 1]} ${y}`;
};

/** "Nov", for a month given as "2026-11". */
export const monthShort = (yearMonth: string): string => MONTHS[Number(yearMonth.split("-")[1]) - 1];

const DAY = 24 * 60 * 60 * 1000;

/** Whole days from the demo date to the date; negative once past. */
export const daysUntil = (iso: string): number =>
    Math.round((parse(iso).getTime() - parse(DEMO_TODAY).getTime()) / DAY);

/** "in 62 days", "in 3 days", "tomorrow", "today", "3 days ago". */
export const relativeDays = (iso: string): string => {
    const days = daysUntil(iso);
    if (days === 0) return "today";
    if (days === 1) return "tomorrow";
    if (days === -1) return "yesterday";
    return days > 0 ? `in ${days} days` : `${-days} days ago`;
};

/** An ISO date `days` after the demo date. */
export const demoDatePlus = (days: number): string => {
    const d = new Date(parse(DEMO_TODAY).getTime() + days * DAY);
    return d.toISOString().slice(0, 10);
};
